import { css } from '@emotion/react'
import { graphql } from 'gatsby'
import { kebabCase } from 'lodash'
import Markdown from 'markdown-to-jsx'
import { type ChangeEventHandler, useId, useRef, useState } from 'react'
import { LuPaperclip, LuX } from 'react-icons/lu'

import { absoluteFill } from '@/theme/mixins'
import { colors } from '@/theme/variables'

import { fieldStyles } from './styles/fieldStyles'

type Props = {
  data: Queries.FormAttachmentFieldFragment | null
  sectionName?: string | null
}

const MAX_TOTAL_BYTES = 18 * 1024 * 1024 // 18 MiB
const MB = 1024 * 1024

const formatSize = (bytes: number) =>
  bytes < MB
    ? `${Math.max(1, Math.round(bytes / 1024))}KB`
    : `${(bytes / MB).toFixed(1)}MB`

const totalSize = (files: File[]) =>
  files.reduce((sum, f) => sum + f.size, 0)

const isSameFile = (a: File, b: File) =>
  a.name === b.name &&
  a.size === b.size &&
  a.lastModified === b.lastModified

export const FormAttachmentField = ({ data, sectionName }: Props) => {
  const name = `ATTACHMENT--${sectionName ? kebabCase(sectionName) + '-' : ''}${data?.label ? kebabCase(data?.label) : ''}`
  const uniqueId = useId()
  const inputRef = useRef<HTMLInputElement>(null)

  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)

  const getFiletypes = () => {
    const options = JSON.parse(
      data?.fileTypes as unknown as string
    ) as string[]
    return options
      .map(option => {
        switch (option) {
          case 'Images':
            return 'image/*'
          case 'Documents':
            return '.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf'
        }
      })
      .filter(Boolean)
      .join(',')
  }

  // Write our file list back into the real input so FormData picks it up
  const syncInput = (next: File[]) => {
    if (!inputRef.current) return
    const dt = new DataTransfer()
    next.forEach(f => dt.items.add(f))
    inputRef.current.files = dt.files
  }

  const handleChange: ChangeEventHandler<HTMLInputElement> = e => {
    const incoming = Array.from(e.currentTarget.files || [])
    setError(null)

    // Picker was cancelled (Chrome clears the input): restore our list
    if (incoming.length === 0) {
      syncInput(files)
      return
    }

    const next = data?.allowMultipleFiles
      ? [
          ...files,
          ...incoming.filter(f => !files.some(x => isSameFile(x, f))),
        ]
      : incoming.slice(0, 1)

    if (totalSize(next) > MAX_TOTAL_BYTES) {
      setError(
        `Attachment failed. The selected files were too large (${(totalSize(next) / MB).toFixed(1)}MB).`
      )
      syncInput(files) // keep the previous selection
      return
    }

    setFiles(next)
    syncInput(next)
  }

  const handleRemove = (index: number) => {
    const next = files.filter((_, i) => i !== index)
    setFiles(next)
    syncInput(next)
    setError(null)
  }

  const styles = {
    container: css`
      position: relative;
      display: flex;
      cursor: pointer;
      justify-content: center;
      align-items: center;
      border-radius: 0.25em;
      background-color: ${colors.gray95};
      border: 1px solid ${colors.gray75};
      color: ${colors.gray45};
      min-height: 1.75em;
      transition: all 300ms ease;
      @media (hover: hover) {
        &:hover {
          background-color: ${colors.red};
          color: #fff;
          border-color: ${colors.redDark};
        }
      }
    `,
    icon: css`
      font-size: 90%;
    `,
    label: css`
      cursor: pointer;
      display: flex;
      gap: 0.25em;
      justify-content: center;
      align-items: center;
    `,
    input: css`
      ${absoluteFill}
      opacity: 0;
      cursor: pointer;
    `,

    list: css`
      list-style: none;
      margin: 0.5em 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.25em;
      width: 100%;
    `,
    listItem: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5em;
      padding: 0.25em 0.5em;
      border-radius: 0.25em;
      background-color: ${colors.gray98};
      border: 1px solid ${colors.gray85};
      color: ${colors.gray45};
      font-size: 0.9em;
    `,
    fileName: css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    `,
    fileSize: css`
      opacity: 0.5;
      font-size: 0.85em;
      margin-left: 0.5em;
      white-space: nowrap;
    `,
    removeButton: css`
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.25em;
      border: 0;
      background: transparent;
      color: inherit;
      cursor: pointer;
      border-radius: 0.25em;
      transition: all 200ms ease;
      @media (hover: hover) {
        &:hover {
          color: ${colors.red};
        }
      }
    `,
    maxSize: css`
      display: block;
      margin: 0.5em 0 0;
      font-size: 0.85em;
      text-align: center;
      width: 100%;
      color: ${colors.gray65};
    `,
    error: css`
      display: block;
      font-size: 0.85em;
      margin: 0.5em 0 0;
      text-align: center;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5em;
      padding: 0.25em 0.5em;
      border-radius: 0.25em;
      border: 1px solid ${colors.redLight};
      color: ${colors.red};
      box-sizing: border-box;
      &:before {
        content: '';
      }
    `,
  }

  return (
    <div css={fieldStyles.container('Full')}>
      <div css={styles.container}>
        <label
          css={styles.label}
          htmlFor={name + uniqueId}
        >
          <LuPaperclip css={styles.icon} />
          <Markdown
            options={{
              wrapper: 'span',
              forceInline: true,
              forceWrapper: true,
            }}
          >
            {data?.label || 'Attach files'}
          </Markdown>
        </label>
        <input
          ref={inputRef}
          css={styles.input}
          id={name + uniqueId}
          name={name}
          accept={getFiletypes()}
          multiple={data?.allowMultipleFiles || false}
          type="file"
          onChange={handleChange}
        />
      </div>
      {files.length > 0 && (
        <ul css={styles.list}>
          {files.map((file, i) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}`}
              css={styles.listItem}
            >
              <span css={styles.fileName}>
                {file.name}
                <span css={styles.fileSize}>
                  {formatSize(file.size)}
                </span>
              </span>
              <button
                type="button"
                css={styles.removeButton}
                aria-label={`Remove ${file.name}`}
                onClick={() => handleRemove(i)}
              >
                <LuX />
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <div css={styles.error}>
          <Markdown
            options={{
              wrapper: 'span',
              forceBlock: false,
              forceWrapper: true,
            }}
          >
            {error}
          </Markdown>
          <button
            type="button"
            css={styles.removeButton}
            aria-label={`Hide error`}
            onClick={() => setError(null)}
          >
            <LuX />
          </button>
        </div>
      )}
      <span css={styles.maxSize}>
        {files.length > 0
          ? `Attachment size: ${formatSize(totalSize(files))} / Maximum ${MAX_TOTAL_BYTES / MB}MB`
          : `Maximum attachment size: ${MAX_TOTAL_BYTES / MB}MB`}
      </span>
    </div>
  )
}

export const FormAttachmentFieldFragment = graphql`
  fragment FormAttachmentField on DatoCmsFormAttachmentField {
    __typename
    id: originalId
    label
    allowMultipleFiles
    fileTypes
  }
`
